import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Chart, TimeScale, TimeSeriesScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { useAuth } from '../contexts/AuthContext';
import MaziLogo from "../images/maxi_logo.png";
import Solarpanel_structure from "../images/solar_panel_structure.png";
import HeroVideoSlider, { VideoSlide } from "../components/HeroVideoSlider";
import Sunshot from "../videos/sunshot.mp4";
import Solarpanel from "../videos/solarpanel.mp4";
import Workerspanel from "../videos/workerspanel.mp4";

Chart.register(TimeScale, TimeSeriesScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const Home: React.FC = () => {
    const { isAuthenticated, logout } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // refs for charts (unchanged)
    const powerChartRef = useRef<Chart | null>(null);
    const financialChartRef = useRef<Chart | null>(null);
    const powerCanvasRef = useRef<HTMLCanvasElement>(null);
    const financialCanvasRef = useRef<HTMLCanvasElement>(null);

    // NEW: refs for menu and button to detect outside clicks
    const menuRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    const setupCharts = () => {
        if (powerChartRef.current) powerChartRef.current.destroy();
        if (financialChartRef.current) financialChartRef.current.destroy();

        Chart.defaults.color = '#9ca3af';
        Chart.defaults.borderColor = 'rgba(107, 114, 128, 0.3)';

        const powerLabels: Date[] = [];
        const actualPower: number[] = [];
        const targetPower: number[] = [];
        let cumulativeActual = 0;
        let cumulativeTarget = 0;
        for (let i = 0; i < 24; i++) {
            const date = new Date(2023, i, 1);
            powerLabels.push(date);
            cumulativeTarget += 320;
            targetPower.push(cumulativeTarget);
            const randomFactor = 0.9 + Math.random() * 0.2;
            cumulativeActual += 320 * randomFactor;
            actualPower.push(cumulativeActual);
        }

        const financialLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
        const revenue = [45, 48, 52, 49, 55, 58, 60, 62];
        const omCosts = [5, 4.5, 5.2, 4.8, 5.5, 5.1, 5.3, 5.4];
        const netRevenue = revenue.map((r, i) => r - omCosts[i]);

        if (powerCanvasRef.current) {
            const powerCtx = powerCanvasRef.current.getContext('2d');
            if (powerCtx) {
                powerChartRef.current = new Chart(powerCtx, {
                    type: 'line',
                    data: { labels: powerLabels as any, datasets: [{ label: 'Actual Generation (MWh)', data: actualPower, borderColor: '#4ade80', backgroundColor: 'rgba(74, 222, 128, 0.1)', fill: true, tension: 0.4, pointRadius: 0 }, { label: 'Target Generation (MWh)', data: targetPower, borderColor: '#6b7280', borderDash: [5, 5], fill: false, pointRadius: 0 }] },
                    options: { responsive: true, maintainAspectRatio: false, scales: { x: { type: 'time', time: { unit: 'quarter' }, grid: { display: false } }, y: { beginAtZero: true, ticks: { callback: (value: any) => `${value / 1000}k` } } }, plugins: { legend: { position: 'top' } } }
                });
            }
        }

        if (financialCanvasRef.current) {
            const financialCtx = financialCanvasRef.current.getContext('2d');
            if (financialCtx) {
                financialChartRef.current = new Chart(financialCtx, {
                    type: 'bar',
                    data: { labels: financialLabels, datasets: [{ label: 'Gross Revenue (Lakhs)', data: revenue, backgroundColor: 'rgba(74, 222, 128, 0.6)', borderColor: '#4ade80', borderWidth: 1 }, { label: 'Net Revenue (Lakhs)', data: netRevenue, type: 'line', borderColor: '#f87171', backgroundColor: '#f87171', tension: 0.4, pointRadius: 2 }] },
                    options: { responsive: true, maintainAspectRatio: false, scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { callback: (value: any) => `₹${value}L` } } }, plugins: { legend: { position: 'top' } } }
                });
            }
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            setTimeout(setupCharts, 100);
        }
    }, [isAuthenticated]);

    // NEW: handle clicks outside menu & Escape to close
    useEffect(() => {
        function handleDocumentClick(e: MouseEvent | TouchEvent) {
            if (!isMobileMenuOpen) return;
            const target = e.target as Node;
            const clickedInsideMenu = menuRef.current?.contains(target);
            const clickedOnButton = buttonRef.current?.contains(target);
            if (!clickedInsideMenu && !clickedOnButton) {
                setIsMobileMenuOpen(false);
            }
        }

        function handleEsc(e: KeyboardEvent) {
            if (e.key === 'Escape') setIsMobileMenuOpen(false);
        }

        document.addEventListener('mousedown', handleDocumentClick);
        document.addEventListener('touchstart', handleDocumentClick);
        document.addEventListener('keydown', handleEsc);

        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
            document.removeEventListener('touchstart', handleDocumentClick);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [isMobileMenuOpen]);

    // Close menu when navigation is clicked
    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    const slides: VideoSlide[] = [
      {
        id: "v1",
        title: "Powering a Brighter Tomorrow with Solar",
        subtitle: "Our 3 MW project helps provide clean, sustainable energy and supports India’s renewable transition",
        videoSrc: Sunshot,
      },
      {
        id: "v2",
        title: "Clean Energy. Real Impact.",
        subtitle: "Mazi’s 3 MW solar project is helping accelerate India’s shift to renewable power.",
        videoSrc: Solarpanel,
      },
      {
        id: "v3",
        title: "Sustainable power for communities",
        subtitle: "Generating enough electricity to power ~4,000 homes.",
        videoSrc: Workerspanel,
      },
    ];

    return (
        <div className="bg-gray-900 text-gray-300">
            <header className="bg-gray-900/70 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-700/50">
                <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <a href="#home" className="flex items-center gap-3">
                        <img src={MaziLogo} alt="Mazi Logo" className="w-32 h-auto" />
                    </a>

                    {!isAuthenticated ? (
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#home" className="text-gray-300 hover:text-green-400 transition-all duration-300">Home</a>
                            <a href="#about" className="text-gray-300 hover:text-green-400 transition-all duration-300">About</a>
                            <a href="#technology" className="text-gray-300 hover:text-green-400 transition-all duration-300">Technology</a>
                            <a href="#impact" className="text-gray-300 hover:text-green-400 transition-all duration-300">Impact</a>
                            <a href="#contact" className="text-gray-300 hover:text-green-400 transition-all duration-300">Contact</a>
                        </div>
                    ) : (
                        <div className="hidden md:flex items-center space-x-8">
                            <Link to="/dashboard" className="text-gray-300 hover:text-green-400 transition-all duration-300">Dashboard</Link>
                            <button onClick={logout} className="bg-red-600 text-white font-semibold px-5 py-2 rounded-lg hover:bg-red-500 transition-all duration-300 transform hover:scale-105">Logout</button>
                        </div>
                    )}

                    {/* MOBILE HAMBURGER */}
                    <div className="md:hidden">
                        <button
                          ref={buttonRef}
                          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                          className="text-gray-300 focus:outline-none"
                          aria-expanded={isMobileMenuOpen}
                          aria-controls="mobile-menu"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
                            </svg>
                        </button>
                    </div>
                </nav>

                {/* MOBILE MENU: fixed overlay so it doesn't unpredictably overlap page content */}
                <div
                  id="mobile-menu"
                  ref={menuRef}
                  className={`${isMobileMenuOpen ? '' : 'hidden'} md:hidden`}
                >
                    <div className="fixed inset-x-0 top-16 bg-gray-900 border-t border-gray-700 z-40">
                        {!isAuthenticated ? (
                            <div>
                                <a href="#home" onClick={closeMobileMenu} className="block px-6 py-3 text-gray-300 hover:bg-gray-800">Home</a>
                                <a href="#about" onClick={closeMobileMenu} className="block px-6 py-3 text-gray-300 hover:bg-gray-800">About</a>
                                <a href="#technology" onClick={closeMobileMenu} className="block px-6 py-3 text-gray-300 hover:bg-gray-800">Technology</a>
                                <a href="#impact" onClick={closeMobileMenu} className="block px-6 py-3 text-gray-300 hover:bg-gray-800">Impact</a>
                                <a href="#contact" onClick={closeMobileMenu} className="block px-6 py-3 text-gray-300 hover:bg-gray-800">Contact</a>
                            </div>
                        ) : (
                            <div>
                                <Link to="/dashboard" onClick={closeMobileMenu} className="block px-6 py-3 text-gray-300 hover:bg-gray-800">Dashboard</Link>
                                {/* ensure logout also closes menu */}
                                <button onClick={() => { logout(); closeMobileMenu(); }} className="w-full text-left block px-6 py-3 text-red-400 font-bold hover:bg-gray-800">Logout</button>
                            </div>
                        )}
                    </div>

                    {/* optional spacer if you want the flow to keep height (not necessary but harmless) */}
                    <div aria-hidden className="h-0" />
                </div>
            </header>

            {!isAuthenticated && (
                <main>
                    <HeroVideoSlider slides={slides} intervalMs={7000} heroMaxWidth="min(760px,86%)" />

                    <section className="py-20 bg-gray-800">
                        <div className="container mx-auto px-6">
                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                                <div className="flex flex-col items-center">
                                    <i className="fas fa-bolt text-4xl text-green-400 mb-4"></i>
                                    <h3 className="text-xl font-bold text-white">3 Mw Capacity</h3>
                                    <p className="text-gray-400 mt-2">Generating enough electricity to power ~4,000 homes.</p>
                                </div>
                                <div className="flex flex-col items-center">
                                    <i className="fas fa-leaf text-4xl text-green-400 mb-4"></i>
                                    <h3 className="text-xl font-bold text-white">Clean Energy</h3>
                                    <p className="text-gray-400 mt-2">Offsetting ~2,800 tons of carbon emissions annually.</p>
                                </div>
                                <div className="flex flex-col items-center">
                                    <i className="fas fa-industry text-4xl text-green-400 mb-4"></i>
                                    <h3 className="text-xl font-bold text-white">Made in India</h3>
                                    <p className="text-gray-400 mt-2">Supporting local manufacturing and the "Make in India" initiative.</p>
                                </div>
                                <div className="flex flex-col items-center">
                                    <i className="fas fa-globe-asia text-4xl text-green-400 mb-4"></i>
                                    <h3 className="text-xl font-bold text-white">Sustainable Future</h3>
                                    <p className="text-gray-400 mt-2">Contributing to a cleaner, greener India for generations to come.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section id="about" className="py-24 bg-gray-900">
                        <div className="container mx-auto px-6">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold text-white">Our Vision: A Sustainable Future, Powered by the Sun</h2>
                            </div>
                            <div className="max-w-4xl mx-auto text-lg text-center text-gray-300 space-y-4">
                                <p>Mazi Green Energy is a 3 MW solar power plant located in Theni. We are a testament to the power of renewable energy and a significant step towards India's ambitious clean energy goals. Our journey began with a simple belief: that a sustainable future is not just a possibility, but a necessity.</p>
                                <p>Our state-of-the-art facility uses cutting-edge photovoltaic technology to convert sunlight directly into electricity. We are proud to be a part of India's vibrant renewable energy sector, contributing to the country's energy security and environmental sustainability.</p>
                                <p className="text-xl font-semibold text-green-400 mt-6">Our Mission: To generate reliable, clean power while fostering a deeper understanding and adoption of renewable energy technologies.</p>
                            </div>
                        </div>
                    </section>

                    <section id="technology" className="py-24 bg-gray-800">
                        <div className="container mx-auto px-6">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold text-white">The Science of Sunshine: How We Generate Clean Power</h2>
                                <p className="text-lg text-gray-400 mt-4 max-w-3xl mx-auto">Our 3 MW plant is built with the highest quality components and advanced technology to ensure maximum efficiency and reliability.</p>
                            </div>
                            <div className="grid md:grid-cols-2 gap-10 items-center">
                                <div className="space-y-8">
                                    <div>
                                        <h3 className="text-2xl font-bold text-green-400 mb-2">Solar Panels</h3>
                                        <p>We use high-efficiency monocrystalline silicon panels, designed to perform optimally in India's unique climate conditions.</p>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-green-400 mb-2">Inverters</h3>
                                        <p>Our advanced inverters convert DC electricity from the panels into grid-compatible AC power with a high conversion efficiency rate.</p>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-green-400 mb-2">Mounting Structure</h3>
                                        <p>Robust mounting structures are engineered to withstand extreme weather, ensuring the longevity and stability of the plant.</p>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-green-400 mb-2">Monitoring System</h3>
                                        <p>A 24/7 remote monitoring system allows us to track performance, identify issues, and ensure a continuous and optimal power output.</p>
                                    </div>
                                </div>
                                <div className="bg-[#0f1a22] border border-[#25303a] rounded-2xl overflow-hidden w-full">
                                  <div className="p-4 md:p-8">
                                    <img
                                      src={Solarpanel_structure}
                                      alt="Solar panel diagram"
                                      className="
                                        w-full               
                                        max-w-[520px]        
                                        h-[220px]            
                                        sm:h-[320px]         
                                        md:h-[360px]         
                                        object-contain       
                                        block mx-auto        
                                      "
                                    />
                                  </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section id="impact" className="py-24 bg-gray-900">
                        <div className="container mx-auto px-6">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold text-white">More Than Just Power: Our Contribution to a Greener India</h2>
                                <p className="text-lg text-gray-400 mt-4 max-w-3xl mx-auto">Our 3 MW solar plant's impact extends far beyond electricity generation. We are a force for positive change, contributing to a better environment and a stronger economy.</p>
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                                <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
                                    <h3 className="text-2xl font-bold text-green-400 mb-3">Environmental Impact</h3>
                                    <ul className="list-disc list-inside space-y-2">
                                        <li><strong>Carbon Offset:</strong> Prevents ~2,800 tons of CO₂ emissions annually.</li>
                                        <li><strong>Equivalent to:</strong> Planting ~7,500 trees each year.</li>
                                        <li><strong>Water Conservation:</strong> Requires virtually no water for power production.</li>
                                    </ul>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
                                    <h3 className="text-2xl font-bold text-green-400 mb-3">Economic Impact</h3>
                                    <ul className="list-disc list-inside space-y-2">
                                        <li><strong>Energy Security:</strong> Reduces reliance on imported fossil fuels.</li>
                                        <li><strong>Job Creation:</strong> Creates local jobs in construction and maintenance.</li>
                                        <li><strong>Stimulates Economy:</strong> Boosts the regional green technology sector.</li>
                                    </ul>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
                                    <h3 className="text-2xl font-bold text-green-400 mb-3">Social Impact</h3>
                                    <ul className="list-disc list-inside space-y-2">
                                        <li><strong>Community Engagement:</strong> Promoting the benefits of renewable energy.</li>
                                        <li><strong>Educational Opportunities:</strong> Hosting tours and workshops for students.</li>
                                        <li><strong>Inspiring Change:</strong> Leading by example for a sustainable future.</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="bg-gray-800 p-8 rounded-xl mt-16">
                                <h3 className="text-3xl font-bold text-white text-center mb-8">Facts and Figures</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                                    <div><p className="text-4xl font-extrabold text-green-400">3 MW</p><p className="text-gray-400 mt-1">Total Capacity</p></div>
                                    <div><p className="text-4xl font-extrabold text-green-400">3,800 MWh</p><p className="text-gray-400 mt-1">Annual Generation</p></div>
                                    <div><p className="text-4xl font-extrabold text-green-400">2,800 tons</p><p className="text-gray-400 mt-1">CO2 Saved Annually</p></div>
                                    <div><p className="text-4xl font-extrabold text-green-400">25 Years</p><p className="text-gray-400 mt-1">Operational Lifetime</p></div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section id="contact" className="py-24 bg-gray-800">
                      <div className="container mx-auto px-6">
                        <div className="text-center mb-16">
                          <h2 className="text-4xl font-bold text-white">Get in Touch With Us</h2>
                          <p className="text-lg text-gray-400 mt-4 max-w-2xl mx-auto">
                            We are always open to questions, collaboration opportunities, and partnerships. We'd love to hear from you.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                          <div className="space-y-4">
                            <h3 className="text-2xl font-bold text-white mb-3">Contact Information</h3>
                            <address className="not-italic text-gray-300 space-y-2">
                              <p className="flex items-start gap-3">
                                <span aria-hidden className="w-6 text-green-400"><i className="fas fa-map-marker-alt" /></span>
                                <span>7A, Gnanasundari Nagar, Ramanathapuram, Coimbatore-641045</span>
                              </p>
                              <p className="flex items-center gap-3">
                                <span aria-hidden className="w-6 text-green-400"><i className="fas fa-phone" /></span>
                                <a href="tel:+919944942979" className="hover:text-green-300 text-gray-300">9944942979</a>
                              </p>
                              <p className="flex items-center gap-3">
                                <span aria-hidden className="w-6 text-green-400"><i className="fas fa-envelope" /></span>
                                <a href="mailto:mazigreenenergy25@gmail.com" className="hover:text-green-300 text-gray-300">mazigreenenergy25@gmail.com</a>
                              </p>
                            </address>
                          </div>

                          <div className="space-y-4">
                            <h3 className="text-2xl font-bold text-white mb-3">Follow Us</h3>
                            <p className="text-gray-300">Stay connected — follow us on social for updates and news.</p>
                            <div className="flex space-x-4 mt-4">
                              <a href="/" aria-label="Twitter" className="text-gray-400 hover:text-green-400">
                                <i className="fab fa-twitter fa-2x" />
                              </a>
                              <a href="/" aria-label="LinkedIn" className="text-gray-400 hover:text-green-400">
                                <i className="fab fa-linkedin-in fa-2x" />
                              </a>
                              <a href="/" aria-label="Instagram" className="text-gray-400 hover:text-green-400">
                                <i className="fab fa-instagram fa-2x" />
                              </a>
                            </div>
                          </div>

                          <div className="rounded-xl overflow-hidden border border-gray-700">
                            <div className="h-64 w-full">
                              <iframe
                                title="Mazi Green Energy Location"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3217.9786834039733!2d76.98532737504496!3d10.989592289172407!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8590006558e4b%3A0xaac7d1f2d7b1acaa!2sGnanasundari%20Nagar!5e1!3m2!1sen!2sin!4v1763356911929!5m2!1sen!2sin"
                                width="100%"
                                height="100%"
                                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                </main>
            )}

            {isAuthenticated && (
                <div className="bg-gray-900">
                    <section id="investor-dashboard-private" className="py-24">
                        <div className="container mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-white">Investor Dashboard</h2>
                            <p className="text-lg text-gray-400 mt-4">Transparent insights into your investment and our collective impact.</p>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                            <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700"><h4 className="text-sm font-semibold text-gray-400">YTD Net Revenue</h4><p className="text-3xl font-bold text-white mt-2">₹ 1.2 Cr</p></div>
                            <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700"><h4 className="text-sm font-semibold text-gray-400">Lifetime Generation</h4><p className="text-3xl font-bold text-white mt-2">8,250 MWh</p></div>
                            <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700"><h4 className="text-sm font-semibold text-gray-400">Plant Availability</h4><p className="text-3xl font-bold text-white mt-2">99.2%</p></div>
                            <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700"><h4 className="text-sm font-semibold text-gray-400">CO₂ Reduction</h4><p className="text-3xl font-bold text-white mt-2">5,775 Tonnes</p></div>
                        </div>
                        <div className="grid lg:grid-cols-2 gap-8">
                            <div className="bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-700">
                                <h3 className="text-2xl font-bold text-white mb-4">Power Generation vs. Target (MWh)</h3>
                                <div className="h-80">
                                    <canvas ref={powerCanvasRef}></canvas>
                                </div>
                            </div>
                            <div className="bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-700">
                                <h3 className="text-2xl font-bold text-white mb-4">Financial Performance (YTD)</h3>
                                <div className="h-80">
                                    <canvas ref={financialCanvasRef}></canvas>
                                </div>
                            </div>
                            <div className="bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-700">
                                <h3 className="text-2xl font-bold text-white mb-4">Live Video Monitoring</h3>
                                <div className="h-80 bg-black flex items-center justify-center">
                                    <p className="text-gray-500">Live video feed coming soon.</p>
                                </div>
                            </div>
                            <div className="bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-700">
                                <h3 className="text-2xl font-bold text-white mb-4">SCADA System Integration</h3>
                                <div className="h-80 bg-black flex items-center justify-center">
                                    <p className="text-gray-500">SCADA system data coming soon.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                </div>
            )}
        </div>
    );
};

export default Home;
